import { useMutation, useQueryClient } from 'react-query';

import type { EditSuggestionProposalBody } from '../../../../backend/src/matter/procedural-timeline/dto/edit-suggestion-proposal.request.dto';
import type { ProceduralTimelineEventSuggestion } from '../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import { APIBaseChronos } from '@/api/hosts';
import { QueryEntity } from '@/api/types';
import useGetFetchConfig, { FetchMethod } from '@/api/useGetFetchConfig';

interface Params {
  matterId: string;
  suggestionId: string;
  proposal: EditSuggestionProposalBody;
}

/**
 * Edit a pending suggestion's proposed values before accepting it. Only the
 * fields present in `proposal` are written; for an Update the edited fields are
 * flagged so accepting applies them. The card refetches with the new values.
 */
export const useEditProceduralSuggestion = () => {
  const { getFetchConfig } = useGetFetchConfig();
  const queryClient = useQueryClient();

  return useMutation<ProceduralTimelineEventSuggestion, Error, Params>(
    async ({ matterId, suggestionId, proposal }) => {
      const res = await fetch(
        `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/suggestions/${suggestionId}/proposal`,
        getFetchConfig({ method: FetchMethod.PATCH, data: proposal }),
      );
      if (!res.ok) {
        const detail = await res.text().catch(() => '');
        throw new Error(detail || 'Failed to edit the suggestion');
      }
      return res.json();
    },
    {
      onSuccess: (_, { matterId }) => {
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineSuggestions, { matterId }]);
        queryClient.invalidateQueries([QueryEntity.ProceduralTimelineEvents, { matterId }]);
      },
    },
  );
};
